import type { UserPublic } from '@accounts/shared';

/**
 * THE ONE web-layer derivation of the two-step state — the same two-line rule the server's
 * `twoFactorState` (users service) derives for the CLI's list-users column, deliberately
 * restated here rather than shared (agreed in Wave 3C: a two-line rule does not warrant a
 * cross-layer restructure).
 *
 * Because it IS a restatement, the two implementations are pinned equivalent by a
 * cross-layer parity test — `server/tests/two-step-state.parity.test.ts` — which walks all
 * three states through both rules and fails if either side moves alone. That test is the
 * only thing keeping the terminal and the panel honest with each other; nothing structural
 * does. If this rule changes, the server rule and that test change in the same commit.
 *
 * Everything on the Security card — chip, note, and whether the Reset action is offered —
 * reads THIS, so the three cannot disagree about which state a person is in.
 *
 *   on    — enabled.
 *   reset — not enabled, but a reset stands unanswered (`twoFactorResetAt` set; a
 *           successful re-enrolment clears it). The demo's amber treatment.
 *   off   — never set up, or turned off by the person themselves.
 *
 * The server's vocabulary for the middle state is `reset-pending`; this layer's is `reset`.
 * The parity test owns that mapping, so the two names can never drift into two meanings.
 */
export type TwoStepState = 'on' | 'reset' | 'off';

export function twoStepStateOf(
  user: Pick<UserPublic, 'twoFactorEnabled' | 'twoFactorResetAt'>,
): TwoStepState {
  if (user.twoFactorEnabled) return 'on';
  return user.twoFactorResetAt !== null ? 'reset' : 'off';
}
